// frontend/src/pages/TermsOfServicePage.jsx
// Terms of Service - UniLedge LLC
// Last Updated: March 2026

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, ExternalLink, AlertTriangle } from 'lucide-react';

const TermsOfServicePage = () => {
  const effectiveDate = "March 5, 2026";
  const termlyUrl = "https://app.termly.io/policy-viewer/policy.html?policyUUID=998ac675-aabd-449e-8ee4-2efd0d499352";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => window.history.back()} className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeft size={20} className="mr-2" />
              <span>Back</span>
            </button>
            <div className="flex items-center">
              <FileText size={20} className="text-blue-600 mr-2" />
              <span className="font-semibold text-gray-900">UniLedge</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
          
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-gray-600">UniLedge LLC</p>
            <p className="text-sm text-gray-500 mt-2">Effective Date: {effectiveDate}</p>
          </div>

          {/* Termly Link Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center">
                <FileText size={20} className="text-blue-600 mr-2" />
                <span className="text-blue-800 font-medium">Looking for the latest updates?</span>
              </div>
              <a 
                href={termlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Latest Terms
                <ExternalLink size={16} className="ml-2" />
              </a>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
            <div className="flex items-start">
              <AlertTriangle size={24} className="text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800 mb-1">Important Legal Notice</p>
                <p className="text-sm text-amber-700">
                  Please read these Terms carefully. By using UniLedge, you agree to binding arbitration and waive your right to participate in class actions. See Section 20 for details.
                </p>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-gray-50 rounded-lg p-4 mb-8">
            <h2 className="font-semibold text-gray-900 mb-3">Contents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-blue-600">
              <a href="#agreement" className="hover:underline">1. Agreement to Terms</a>
              <a href="#description" className="hover:underline">2. Description of Service</a>
              <a href="#eligibility" className="hover:underline">3. Eligibility</a>
              <a href="#account" className="hover:underline">4. Account Registration</a>
              <a href="#group-purchasing" className="hover:underline">5. Group Purchasing</a>
              <a href="#fees" className="hover:underline">6. Fees and Payments</a>
              <a href="#delivery" className="hover:underline">7. Delivery and Pickup</a>
              <a href="#trust-score" className="hover:underline">8. Trust Score System</a>
              <a href="#tokens" className="hover:underline">9. UniLedge Tokens</a>
              <a href="#jobs" className="hover:underline">10. Job Marketplace</a>
              <a href="#vendors" className="hover:underline">11. Vendors and Products</a>
              <a href="#conduct" className="hover:underline">12. User Conduct</a>
              <a href="#referral" className="hover:underline">13. Referral Program</a>
              <a href="#communications" className="hover:underline">14. Communications Consent</a>
              <a href="#ip" className="hover:underline">15. Intellectual Property</a>
              <a href="#disclaimers" className="hover:underline">16. Disclaimers</a>
              <a href="#liability" className="hover:underline">17. Limitation of Liability</a>
              <a href="#indemnification" className="hover:underline">18. Indemnification</a>
              <a href="#force-majeure" className="hover:underline">19. Force Majeure</a>
              <a href="#arbitration" className="hover:underline">20. Dispute Resolution</a>
              <a href="#governing-law" className="hover:underline">21. Governing Law</a>
              <a href="#modifications" className="hover:underline">22. Modifications</a>
              <a href="#termination" className="hover:underline">23. Termination</a>
              <a href="#general" className="hover:underline">24. General Provisions</a>
              <a href="#contact" className="hover:underline">25. Contact Us</a>
            </div>
          </div>

          {/* Sections */}
          <div className="prose prose-gray max-w-none">
            
            {/* Section 1 */}
            <section id="agreement" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-700 mb-4">
                These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and UniLedge LLC ("UniLedge," "we," "us," or "our") governing your access to and use of the UniLedge platform, including our website (uniledge.io), mobile applications, and all related services (collectively, the "Platform").
              </p>
              <p className="text-gray-700 font-semibold mb-4">
                BY ACCESSING OR USING THE PLATFORM, YOU AGREE TO BE BOUND BY THESE TERMS, OUR PRIVACY POLICY, AND ALL APPLICABLE LAWS AND REGULATIONS. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE PLATFORM.
              </p>
            </section>

            {/* Section 2 */}
            <section id="description" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                UniLedge is a group purchasing and economic coordination platform that enables communities to pool their buying power to achieve bulk savings on everyday essentials.
              </p>
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Core Services</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Group buying coordination using H3 hexagonal geographic indexing</li>
                <li>Connections between buyers and vendors offering bulk pricing</li>
                <li>A token-based rewards system for platform participation</li>
                <li>A job marketplace for community members to earn tokens</li>
                <li>Order fulfillment and delivery coordination</li>
                <li>AI-powered assistance through AVA</li>
              </ul>
              <p className="text-gray-700">
                UniLedge acts as a marketplace facilitator and does not manufacture, store, or directly sell the products listed. Products are provided by third-party vendors.
              </p>
            </section>

            {/* Section 3 */}
            <section id="eligibility" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. Eligibility</h2>
              <p className="text-gray-700 mb-2">To use the Platform, you must:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Be at least 18 years of age, OR be between 13 and 17 years of age with verifiable parental or guardian consent</li>
                <li>Have the legal capacity to enter into a binding contract</li>
                <li>Not be prohibited from using the Platform under applicable law</li>
                <li>Not have been previously banned from the Platform</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section id="account" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Account Registration and Security</h2>
              <p className="text-gray-700 mb-2">To access certain features, you must create an account. You agree to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security and confidentiality of your password</li>
                <li>Not share your account credentials with any third party</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
              </ul>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or have a Trust Score below acceptable thresholds.
              </p>
            </section>

            {/* Section 5 */}
            <section id="group-purchasing" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Group Purchasing</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">5.1 How Group Buying Works</h3>
              <p className="text-gray-700 mb-4">
                UniLedge automatically groups orders from users in the same geographic area (H3 zone) for the same products within a specified time window. You do not need to manually coordinate with other buyers. As more users purchase the same product, the price tier may decrease.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">5.2 Pricing Tiers</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li><strong>5+ buyers:</strong> 8-10% discount from retail</li>
                <li><strong>10+ buyers:</strong> 16-19% discount from retail</li>
                <li><strong>25+ buyers:</strong> 23-30% discount from retail</li>
                <li><strong>50+ buyers:</strong> 35-40% discount from retail</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">5.3 Payment Authorization</h3>
              <p className="text-gray-700 mb-4">
                By completing checkout, you authorize UniLedge to charge your payment method. If a group does not reach its minimum threshold, the order will be cancelled and you will not be charged (or receive a full refund).
              </p>
            </section>

            {/* Section 6 */}
            <section id="fees" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Fees and Payments</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.1 Platform Fees</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li><strong>Transaction Fee:</strong> 7.5% of the purchase amount</li>
                <li><strong>Logistics Fee:</strong> Up to 7.5% for coordination and delivery services</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.2 Payment Processing</h3>
              <p className="text-gray-700 mb-4">
                Payments are processed securely through Stripe, Inc. By making a purchase, you agree to Stripe's terms of service. UniLedge does not store your complete payment card information.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.3 Refunds</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li><strong>Before group threshold met:</strong> Full refund available</li>
                <li><strong>After threshold met, before shipping:</strong> Refund minus restocking fee (up to 15%)</li>
                <li><strong>After shipping:</strong> Subject to vendor return policy</li>
                <li><strong>Defective or incorrect items:</strong> Full refund or replacement</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section id="delivery" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Delivery and Pickup</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li><strong>Standard Delivery:</strong> $5.99 fee, delivered to your address</li>
                <li><strong>Pickup:</strong> Free, collect from designated pickup point</li>
              </ul>
              <p className="text-gray-700">
                Choosing pickup earns you bonus tokens (10% of base tokens, minimum 5, maximum 50) plus you save the delivery fee.
              </p>
            </section>

            {/* Section 8 */}
            <section id="trust-score" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Trust Score System</h2>
              <p className="text-gray-700 mb-4">
                UniLedge uses a Trust Score system (0.0 to 5.0 scale) to maintain platform integrity.
              </p>
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Factors Affecting Trust Score</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Order completion rate</li>
                <li>Payment reliability</li>
                <li>Job performance (for workers)</li>
                <li>Community ratings and feedback</li>
                <li>Policy compliance</li>
              </ul>
              <p className="text-gray-700">
                A low Trust Score may result in reduced access to features, ineligibility for jobs, lower token redemption limits, or account suspension.
              </p>
            </section>

            {/* Section 9 - Tokens */}
            <section id="tokens" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. UniLedge Tokens</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-800">
                  UniLedge tokens are a digital reward currency used exclusively within the Platform. 10 tokens = $1.00 in redemption value.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">9.1 Earning Tokens</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Welcome bonus: 50 tokens for new users</li>
                <li>Profile completion: 20 tokens</li>
                <li>Purchases: Variable tokens based on order value</li>
                <li>Referrals: 25 tokens when a referred friend makes first purchase</li>
                <li>Referral earnings: 10% of referred user's token earnings for 90 days</li>
                <li>Jobs: 15-50 tokens depending on job type</li>
                <li>Achievements: Bonus tokens for completing milestones</li>
                <li>Pickup bonus: 10% extra tokens when choosing pickup</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">9.2 HYBRID Redemption System</h3>
              <p className="text-gray-700 mb-2">
                You can redeem tokens up to the <strong>LESSER OF</strong>:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Your tier's percentage cap of your cart total, OR</li>
                <li>Your loyalty tier's maximum dollar cap</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Loyalty Tier Redemption Caps</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li><strong>New User:</strong> 10% / $15 maximum per order</li>
                <li><strong>Bronze (5+ purchases):</strong> 15% / $25 maximum per order</li>
                <li><strong>Silver (15+ purchases, 2+ referrals):</strong> 20% / $40 maximum per order</li>
                <li><strong>Gold (30+ purchases, 5+ referrals):</strong> 30% / $75 maximum per order</li>
                <li><strong>Platinum (50+ purchases, 10+ referrals):</strong> 40% UNCAPPED</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">9.3 Token Expiration</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li><strong>New User:</strong> 90 days of inactivity</li>
                <li><strong>Bronze:</strong> 180 days of inactivity</li>
                <li><strong>Silver:</strong> 1 year of inactivity</li>
                <li><strong>Gold:</strong> 2 years of inactivity</li>
                <li><strong>Platinum:</strong> Tokens NEVER expire</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Important:</strong> ANY platform activity (purchase, job, referral, login) resets your expiration clock.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">9.4 Token Restrictions</h3>
              <p className="text-gray-700 mb-2">
                <strong>Tokens have NO cash value and cannot be:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Exchanged for cash or cryptocurrency</li>
                <li>Transferred to bank accounts</li>
                <li>Sold to third parties</li>
                <li>Used outside the UniLedge Platform</li>
              </ul>
            </section>

            {/* Section 10 */}
            <section id="jobs" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Job Marketplace</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">10.1 Independent Contractor Status</h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800">
                  <strong>IMPORTANT:</strong> Users who perform jobs through the Platform do so as independent contractors, NOT as employees of UniLedge. You are responsible for your own taxes, insurance, and compliance with applicable laws.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">10.2 Job Requirements</h3>
              <p className="text-gray-700 mb-4">
                Certain jobs require specific skills, certifications, or minimum Trust Scores. By claiming a job, you represent that you possess all required qualifications.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">10.3 Job Verification</h3>
              <p className="text-gray-700">
                Workers may be required to submit verification photos with GPS metadata to confirm job completion.
              </p>
            </section>

            {/* Section 11 */}
            <section id="vendors" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Vendors and Products</h2>
              <p className="text-gray-700 mb-4">
                Products on the Platform are provided by third-party vendors. While we vet vendors and enforce quality standards, UniLedge is NOT liable for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Product quality, safety, or accuracy of descriptions</li>
                <li>Vendor compliance with applicable laws</li>
                <li>Product defects or recalls</li>
                <li>Delays caused by vendors</li>
              </ul>
            </section>

            {/* Section 12 */}
            <section id="conduct" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. User Conduct</h2>
              <p className="text-gray-700 mb-2">You agree NOT to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Use the Platform for any unlawful purpose</li>
                <li>Harass, abuse, threaten, or harm other users</li>
                <li>Provide false, misleading, or inaccurate information</li>
                <li>Create multiple accounts to circumvent restrictions</li>
                <li>Manipulate the token system, Trust Scores, or pricing</li>
                <li>Use bots, scrapers, or automated systems without permission</li>
                <li>Attempt to gain unauthorized access to the Platform</li>
                <li>Resell products for commercial profit without authorization</li>
              </ul>
            </section>

            {/* Section 13 */}
            <section id="referral" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">13. Referral Program</h2>
              <p className="text-gray-700 mb-2">Referral bonuses are subject to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Referred user must be a genuine new user</li>
                <li>Referred user must complete a valid first purchase</li>
                <li>Self-referrals and referral fraud result in forfeiture and possible termination</li>
                <li>UniLedge reserves the right to audit and reverse fraudulent bonuses</li>
              </ul>
            </section>

            {/* Section 14 */}
            <section id="communications" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">14. Communications Consent</h2>
              <p className="text-gray-700 mb-2">By creating an account, you consent to receive communications via:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Email (order confirmations, account alerts, promotional messages)</li>
                <li>SMS/Text messages (order updates, verification codes)</li>
                <li>Push notifications (if enabled)</li>
              </ul>
              <p className="text-gray-700">
                You may opt out of promotional communications at any time. Standard message and data rates may apply for SMS.
              </p>
            </section>

            {/* Section 15 */}
            <section id="ip" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">15. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                The Platform and its original content, features, and functionality are owned by UniLedge and protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-gray-700">
                By uploading content to the Platform, you grant UniLedge a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display that content in connection with operating the Platform.
              </p>
            </section>

            {/* Section 16 */}
            <section id="disclaimers" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">16. Disclaimers</h2>
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <p className="text-gray-800 font-semibold">
                  THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
                </p>
              </div>
            </section>

            {/* Section 17 */}
            <section id="liability" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">17. Limitation of Liability</h2>
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-4">
                <p className="text-gray-800 font-semibold mb-2">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, UNILEDGE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
                </p>
                <p className="text-gray-800 font-semibold">
                  OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF: (A) THE AMOUNT YOU PAID TO UNILEDGE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
                </p>
              </div>
            </section>

            {/* Section 18 */}
            <section id="indemnification" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">18. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify, defend, and hold harmless UniLedge from any claims, damages, losses, or expenses arising from your use of the Platform, violation of these Terms, or violation of any third-party rights.
              </p>
            </section>

            {/* Section 19 */}
            <section id="force-majeure" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">19. Force Majeure</h2>
              <p className="text-gray-700">
                UniLedge shall not be liable for any failure or delay due to circumstances beyond our reasonable control, including natural disasters, pandemics, war, terrorism, government actions, supply chain disruptions, or acts of God.
              </p>
            </section>

            {/* Section 20 - Arbitration */}
            <section id="arbitration" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">20. Dispute Resolution and Arbitration</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 font-semibold">
                  PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">20.1 Informal Resolution</h3>
              <p className="text-gray-700 mb-4">
                Before filing any formal dispute, you agree to contact us at legal@uniledge.io and attempt to resolve the dispute informally for at least 30 days.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">20.2 Binding Arbitration</h3>
              <p className="text-gray-700 mb-4">
                If informal resolution fails, any dispute shall be resolved through binding arbitration administered by the American Arbitration Association (AAA). Arbitration will be conducted in Sacramento County, California.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">20.3 Class Action Waiver</h3>
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <p className="text-gray-800 font-semibold">
                  YOU AND UNILEDGE AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
                </p>
              </div>
            </section>

            {/* Section 21 */}
            <section id="governing-law" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">21. Governing Law</h2>
              <p className="text-gray-700">
                These Terms shall be governed by the laws of the State of California, without regard to conflict of law provisions. To the extent litigation is permitted, you consent to the exclusive jurisdiction of courts in Sacramento County, California.
              </p>
            </section>

            {/* Section 22 */}
            <section id="modifications" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">22. Modifications to Terms</h2>
              <p className="text-gray-700 mb-2">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
                <li>Posting the updated Terms on the Platform</li>
                <li>Updating the "Effective Date"</li>
                <li>Sending you an email notification</li>
              </ul>
              <p className="text-gray-700">
                Your continued use after changes take effect constitutes acceptance.
              </p>
            </section>

            {/* Section 23 */}
            <section id="termination" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">23. Termination</h2>
              <p className="text-gray-700 mb-4">
                <strong>Termination by You:</strong> You may terminate your account at any time by contacting support@uniledge.io. Unused tokens will expire according to your tier's policy.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Termination by UniLedge:</strong> We may terminate your account immediately for breach of Terms, fraudulent activity, or extended inactivity.
              </p>
              <p className="text-gray-700">
                Upon termination, your right to use the Platform ceases immediately. Sections that should survive (Disclaimers, Liability, Indemnification, Arbitration) shall survive termination.
              </p>
            </section>

            {/* Section 24 */}
            <section id="general" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">24. General Provisions</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Entire Agreement:</strong> These Terms and our Privacy Policy constitute the entire agreement between you and UniLedge.</li>
                <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions continue in effect.</li>
                <li><strong>Waiver:</strong> Our failure to enforce any right shall not constitute a waiver.</li>
                <li><strong>Assignment:</strong> You may not assign these Terms without our consent. We may assign without restriction.</li>
                <li><strong>No Third-Party Beneficiaries:</strong> These Terms do not create third-party beneficiary rights.</li>
              </ul>
            </section>

            {/* Section 25 */}
            <section id="contact" className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">25. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-2">UniLedge LLC</p>
                <div className="space-y-2 text-gray-700">
                  <p className="flex items-center">
                    <Mail size={16} className="mr-2 text-gray-500" />
                    Legal Inquiries: <a href="mailto:legal@uniledge.io" className="text-blue-600 hover:underline ml-1">legal@uniledge.io</a>
                  </p>
                  <p className="flex items-center">
                    <Mail size={16} className="mr-2 text-gray-500" />
                    General Support: <a href="mailto:support@uniledge.io" className="text-blue-600 hover:underline ml-1">support@uniledge.io</a>
                  </p>
                  <p className="flex items-center">
                    <Mail size={16} className="mr-2 text-gray-500" />
                    Privacy Inquiries: <a href="mailto:privacy@uniledge.io" className="text-blue-600 hover:underline ml-1">privacy@uniledge.io</a>
                  </p>
                  <p className="flex items-center">
                    <ExternalLink size={16} className="mr-2 text-gray-500" />
                    Website: <a href="https://uniledge.io" className="text-blue-600 hover:underline ml-1">https://uniledge.io</a>
                  </p>
                </div>
              </div>
            </section>

          </div>

          {/* Termly Link Banner (Bottom) */}
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mt-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="text-gray-600 text-sm">
                These terms are automatically updated to comply with the latest regulations.
              </span>
              <a 
                href={termlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:underline text-sm font-medium"
              >
                View on Termly
                <ExternalLink size={14} className="ml-1" />
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
            <p>© {new Date().getFullYear()} UniLedge LLC. All rights reserved.</p>
            <div className="mt-2">
              <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>
              <span className="mx-2">|</span>
              <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
              <span className="mx-2">|</span>
              <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;